import { z } from 'zod';
import { LIMITS } from './constants.js';

/** Parent / admin email + password auth. */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email()
  .max(254);

export const parentPasswordSchema = z
  .string()
  .min(LIMITS.MIN_PARENT_PASSWORD_LEN, `Use at least ${LIMITS.MIN_PARENT_PASSWORD_LEN} characters`)
  .max(200);

export const parentRegisterSchema = z.object({
  email: emailSchema,
  password: parentPasswordSchema,
  displayName: z.string().trim().min(1).max(80),
  /**
   * Optional family invite code. When present and valid, the new adult joins
   * that existing family (with the invite's role) instead of getting their own.
   */
  inviteCode: z.string().trim().min(8).max(64).optional(),
  /**
   * COPPA: the registering grown-up must give explicit verifiable parental
   * consent (the "email-plus" method completes via the confirmation email). The
   * client must send `true`; the server records a dated consent on the account.
   */
  consent: z.literal(true),
});
export type ParentRegisterInput = z.infer<typeof parentRegisterSchema>;

/** Confirming the email-plus consent via the link in the direct-notice email. */
export const confirmConsentSchema = z.object({
  token: z.string().trim().min(10).max(200),
});
export type ConfirmConsentInput = z.infer<typeof confirmConsentSchema>;

export const parentLoginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
});
export type ParentLoginInput = z.infer<typeof parentLoginSchema>;

/** Confirming email ownership via the link sent at sign-up (or a resend). */
export const verifyEmailSchema = z.object({
  token: z.string().trim().min(10).max(200),
});
export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;

/** "Forgot password" — request a reset link. Always responds ok (no enumeration). */
export const requestPasswordResetSchema = z.object({
  email: emailSchema,
});
export type RequestPasswordResetInput = z.infer<typeof requestPasswordResetSchema>;

/** Confirming a password reset via the link's token + choosing a new password. */
export const confirmPasswordResetSchema = z.object({
  token: z.string().trim().min(10).max(200),
  newPassword: parentPasswordSchema,
});
export type ConfirmPasswordResetInput = z.infer<typeof confirmPasswordResetSchema>;

/** Kid login: screen name (within a family handle) + numeric PIN. No email. */
export const screenNameSchema = z
  .string()
  .trim()
  .min(LIMITS.MIN_SCREEN_NAME_LEN)
  .max(LIMITS.MAX_SCREEN_NAME_LEN)
  .regex(/^[A-Za-z0-9 _-]+$/, 'Letters, numbers, spaces, - and _ only');

export const kidPinSchema = z
  .string()
  .length(LIMITS.KID_PIN_LEN)
  .regex(/^\d+$/, 'PIN must be digits');

/**
 * Reject trivially-guessable PINs (all-same digit like 000000, or a straight
 * run like 123456 / 654321). Used only when SETTING a PIN — login keeps the
 * lenient `kidPinSchema` so an existing kid is never locked out by this rule.
 */
export function isWeakPin(pin: string): boolean {
  if (!/^\d+$/.test(pin)) return false;
  if (/^(\d)\1*$/.test(pin)) return true; // every digit identical
  const ascending = '0123456789';
  const descending = '9876543210';
  return ascending.includes(pin) || descending.includes(pin);
}

/** PIN schema for parent-chosen PINs: same shape, but rejects weak values. */
export const kidPinSetSchema = kidPinSchema.refine((p) => !isWeakPin(p), {
  message: 'Choose a less guessable PIN (avoid 000000, 123456, and the like).',
});

export const kidLoginSchema = z.object({
  /** Short family code printed on the parent dashboard, e.g. "SUNNY-FOX". */
  familyCode: z.string().trim().min(3).max(32),
  screenName: screenNameSchema,
  pin: kidPinSchema,
});
export type KidLoginInput = z.infer<typeof kidLoginSchema>;

/**
 * Redeem a per-kid magic link: the raw token from the link is the only secret.
 * Exchanging it signs the kid into ONE room (a room-scoped session) without a
 * PIN. The server re-validates the token, the kid, and the room.
 */
export const kidMagicSchema = z.object({
  token: z.string().trim().min(10).max(200),
});
export type KidMagicInput = z.infer<typeof kidMagicSchema>;

/** Public, non-sensitive view of an authenticated principal. */
export const sessionUserSchema = z.object({
  id: z.string(),
  role: z.enum(['parent', 'kid', 'admin']),
  displayName: z.string(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;
